import { BehaviorSubject, Subject } from 'rxjs';

import { Injectable, TemplateRef } from '@angular/core';
import { IPageHeaderMobileMenuItem, IPageHeaderTab } from './page-header.component';
import { PhxBadgeContrast, PhxBadgeIntent } from 'phx-core';

export interface IHeaderChanges {
  breadcrumbs?: IBreadcrumb;
  tabs?: IPageHeaderTab[];
  rightExtras?: TemplateRef<unknown>;
  shouldShowBorder?: boolean;
  shouldShowShadow?: boolean;
  isFormHeader?: boolean;
  shouldHideActionsInMobileView?: boolean;
  onFormCancel?: () => void;
  onFormSubmit?: () => void;
  isSubmitActionDisabled?: boolean;
  mobileMenuItems?: IPageHeaderMobileMenuItem[];
  shouldAlwaysShowMobileFooter?: boolean;
  shouldMatchExactLink?: boolean;
  hideBreadcrumbs?: boolean;
  shouldHideShadowAndBorder?: boolean;
  hideAdminSwitcher?: boolean;
}

export interface IHeaderBadge {
  intent?: PhxBadgeIntent;
  contrast?: PhxBadgeContrast;
  text: string;
}

export interface IBreadcrumbPart {
  title: string;
  commands?: string | any[];
  truncateText?: boolean;
  isBetaTag?: boolean;
  customTag?: IHeaderBadge;
}

export interface IBorderAndShadow {
  border: boolean,
  shadow: boolean,
}

/**
 * Interface for tracking a particular intersectionElement located inside a
 * scrollContainer (scrollContainer would be a parent of the intersectionElement).
 *
 */
export interface IHeaderShadowOnContentScrollElems {
  /**
   * The element which is acting as a scroll container for your content area.
   *
   * The content inside this element is scrollable.
   *
   * This element would probably have a fixed height and overflow
   * set as true so that you can scroll inside it.
   *
   * This should be a parent of the intersectionElement in your DOM.
   */
  scrollContainer: HTMLElement;
  /**
   * The element which when completely passes the header component while scrolling,
   * would cause the bottom border on the header to switch to a box-shadow instead.
   *
   * This could be a filter row or an information row on top of which the header component
   * was stacking i.e. it had a bottom border instead of a shadow.
   *
   * This should be a child element of the scrollContainer inside your DOM.
   */
  intersectionElement: HTMLElement;
}

export type IBreadcrumb = IBreadcrumbPart[];

@Injectable({providedIn: 'root'})
export class PageHeaderService {
  public tabs: IPageHeaderTab[] = [];
  public changeHeaderContent$: BehaviorSubject<IHeaderChanges> = new BehaviorSubject<IHeaderChanges>(undefined);
  public showHeader$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public mobileMenuItem$: Subject<IPageHeaderMobileMenuItem> = new Subject<IPageHeaderMobileMenuItem>();
  public removeMobileMenuItem$: Subject<string> = new Subject<string>();
  public changeCurrentTabIndex$: BehaviorSubject<number> = new BehaviorSubject<number>(undefined);
  public showShadowOnContentScroll$: BehaviorSubject<IHeaderShadowOnContentScrollElems>
    = new BehaviorSubject<IHeaderShadowOnContentScrollElems>(null);
  public changeBorderAndShadow$: BehaviorSubject<IBorderAndShadow> =
    new BehaviorSubject<IBorderAndShadow>({border: true, shadow: true});
  public refreshTabsObservable$: Subject<void> = new Subject();

  public get hasTabs(): boolean {
    return this.tabs && !!this.tabs.length;
  }

  public setHeaderContent(headersInfo: IHeaderChanges): void {
    this.tabs = headersInfo.tabs;
    this.changeHeaderContent$.next(headersInfo);
  }

  public addMobileMenuItem(item: IPageHeaderMobileMenuItem): void {
    this.mobileMenuItem$.next(item);
  }

  public removeMobileMenuItem(mobileMenuItemKey: string): void {
    this.removeMobileMenuItem$.next(mobileMenuItemKey);
  }

  public resetHeader(): void {
    this.changeHeaderContent$.next(undefined);
  }

  public setActiveTab(activeTabIndex: number): void {
    this.changeCurrentTabIndex$.next(activeTabIndex);
  }

  public activateTab(activeTabIndex: number): void {
    this.tabs[activeTabIndex].isActive = true;
  }

  public setShadowOnContentScrollElems(elems: IHeaderShadowOnContentScrollElems): void {
    this.showShadowOnContentScroll$.next(elems);
  }

  public setHeaderVisibility(showHeader: boolean): void {
    this.showHeader$.next(showHeader);
  }

  // TODO: Need to remove this stream after implementing AT-5664
  public setBorderAndShadow(border: boolean = false, shadow: boolean = false): void {
    this.changeBorderAndShadow$.next({border,shadow});
  }
}